import React, { useEffect, useMemo, useRef, useState } from "react";
import { ActionSuccessDialog } from "../../../common/dashboard/ActionSuccessDialog";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  CmpEventPaymentStatusEnum,
  CmpEventStatusEnum,
  ExportCmpEventAttendeesCsvQuery,
  useDownloadCmpInvoicePdfMutation,
  CmpEventSponsorshipTaxModeEnum,
} from "../../../../graphql/types";
import { commonStyles } from "../../../common/commonStyles";
// POL-005/POL-006 consolidation (B8/B9): display formatting comes from the
// shared dashboard formatters; editable-value unit conversion comes from
// currencyUnits (rounding parity verified — both Math.round, clamp >= 0).
import { formatCurrencyFromCents } from "../../../common/dashboard/formatters";
import {
  centsToCurrencyValue,
  currencyValueToCents,
} from "../../../common/dashboard/currencyUnits";
import { CurrencyInput } from "../../../common/dashboard/CurrencyInput";
import { SectionShell } from "../../../common/dashboard/SectionShell";
import {
  ClearFiltersButton,
  FilterBar,
  FilterMenuButton,
  FilterSearchField,
  clearFiltersButtonSx,
  filterFieldSx,
} from "../../../common/dashboard/FilterBar";
import { InvoiceSummaryCard } from "../../Invoicing/InvoiceMockComponents";
import ProcessPaymentDialogShell from "../../Invoicing/ProcessPaymentDialogShell";
import type { StaffPaymentMethodOption } from "../../Invoicing/staffPaymentMethods";
import {
  InvoiceDialogCloseButton,
  invoiceDesignFontFamily,
  invoiceDesignTokens,
  invoiceDialogPaperSx,
  invoiceModalActionSx,
  invoiceProcessPaymentFieldSx,
} from "../../Invoicing/InvoiceDesignSystem";
import GoogleAddressTextField from "../../../common/GoogleAddressTextField";
import CountrySelectField from "../../../common/dashboard/CountrySelectField";
import CalendarDateInput, { isCalendarDateValue } from "../../../common/dashboard/CalendarDateInput";
import InvoiceSearchableSelect from "../../Invoicing/InvoiceSearchableSelect";
import type { EventAttendeeChoiceFields } from "../eventBackendMappers";
import {
  downloadBase64File,
  downloadTextFile,
} from "../../Invoicing/invoiceFileDownloads";
import { eventAttendeeListCsvFilename } from "./eventSalesExports";
import { isValidEventSalesEmail } from "./eventSalesBilling";
import {
  getEventAttendeeTicketGroupSeatCount,
  groupEventAttendeesByTicketType,
  type EventAttendeeTicketGroupHeader,
} from "./eventAttendeeTicketGroups";
import { isComplimentaryEventPaymentStatus } from "./eventInvoiceStatus";
import { getStaffTicketEventEligibility } from "./eventInvoiceCreateState";
import EventAttendeeDetailFields from "./EventAttendeeDetailFields";
import { formatStaffPhoneInput } from "../../../../utils/memberContactFormatting";
import type { EventAttendeeSuggestion } from "./eventAttendeeSuggestions";
import {
  getLocalDateInputValue,
  localDateInputToUtcMilli,
} from "../../Invoicing/invoiceFormatting";
import { eventSalesCallerHandledApolloContext } from "./eventSalesApolloErrorPolicy";
import { buildStaffCheckFirstFallback, buildStaffErrorFallback, getUserFacingErrorText } from "../../../../utils/userFacingError";

// Re-exported for the existing consumer fan-out; canonical values live in
// commonStyles (POL-006 token consolidation).
export const cpaGold = commonStyles.cpaGold;
export const cpaGoldDark = commonStyles.cpaGoldDark;

// ---------------------------------------------------------------------------
// Shared button / field sx (B9 plan 10 bounded absorption — both sales pages
// consume these; values are B8's page-local constants promoted verbatim)
// ---------------------------------------------------------------------------

export const goldButtonSx = {
  boxShadow: "none",
  backgroundColor: cpaGold,
  color: "white",
  fontWeight: 700,
  textTransform: "none",
  "&:hover": { backgroundColor: cpaGoldDark, boxShadow: "none" },
} as const;

// Sales table Actions column buttons (design: 12px/600, 7px 10px, 5px radius)
// with a 210px Actions col so "Update Attendee Details" stays on one line.
export const salesActionCellButtonsSx = {
  "& .MuiButton-root": {
    borderRadius: "5px",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "normal",
    lineHeight: "normal",
    minWidth: 0,
    padding: "7px 10px",
    whiteSpace: "nowrap",
  },
} as const;

export const blackButtonSx = {
  boxShadow: "none",
  backgroundColor: "#111827",
  color: "white",
  textTransform: "none",
  "&:hover": { backgroundColor: "#000000" },
} as const;

export const redButtonSx = {
  boxShadow: "none",
  backgroundColor: "#F31723",
  color: "white",
  textTransform: "none",
  "&:hover": { backgroundColor: "#D90F1A" },
} as const;

// Gold-focus editable field + grey disabled fill (mock tokens: #8F969E
// placeholder grey, #F3F4F6 disabled fill).
export const goldFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    "&.Mui-focused fieldset": {
      borderColor: cpaGold,
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8F969E",
    opacity: 1,
  },
} as const;

export const disabledFieldSx = {
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#EEEEEE",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8F969E",
    opacity: 1,
  },
} as const;

const eventBillingFieldSx = {
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e5e5 !important" },
  "& .MuiInputBase-root": {
    height: 40,
    minHeight: "40px !important",
  },
  "& .MuiInputBase-input": {
    padding: "10px 12px",
  },
  "& .MuiFormHelperText-root": {
    color: "#E24C4C",
    fontSize: 12,
    lineHeight: "16px",
    margin: "4px 0 0",
  },
} as const;

// ---------------------------------------------------------------------------
// Currency + value helpers (cents are the backend source of truth)
// ---------------------------------------------------------------------------

// Legacy names kept for the existing consumer fan-out; the implementations
// now live in common/dashboard/currencyUnits (they were promoted verbatim
// from here, so behavior is identical).
export const dollarsToCents = currencyValueToCents;
export const centsToDollarsString = centsToCurrencyValue;

export { formatCurrencyFromCents };

type InvoiceTaxCategorySettingsForDisplay = {
  enabled?: boolean | null;
  firstTaxRateLabel?: string | null;
  firstTaxRatePercentage?: number | null;
  secondTaxRateLabel?: string | null;
  secondTaxRatePercentage?: number | null;
};

const getPositiveTaxRatePercentage = (value: number | null | undefined) =>
  Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : 0;

export const getConfiguredInvoiceTaxRatePercentage = (
  taxSettings?: InvoiceTaxCategorySettingsForDisplay | null,
) => {
  if (!taxSettings?.enabled) {
    return 0;
  }

  return (
    getPositiveTaxRatePercentage(taxSettings.firstTaxRatePercentage) +
    getPositiveTaxRatePercentage(taxSettings.secondTaxRatePercentage)
  );
};

export const getConfiguredInvoiceTaxRateLabel = (
  taxSettings?: InvoiceTaxCategorySettingsForDisplay | null,
) => {
  if (!taxSettings?.enabled) {
    return "Tax";
  }

  const labels = [
    getPositiveTaxRatePercentage(taxSettings.firstTaxRatePercentage) > 0
      ? taxSettings.firstTaxRateLabel?.trim()
      : "",
    getPositiveTaxRatePercentage(taxSettings.secondTaxRatePercentage) > 0
      ? taxSettings.secondTaxRateLabel?.trim()
      : "",
  ].filter((label): label is string => Boolean(label));

  return labels.length ? labels.join(" + ") : "Tax";
};

export const splitName = (
  fullName: string,
): { first: string; last: string } => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { first: parts[0] || "", last: "" };
  }
  return { first: parts[0], last: parts.slice(1).join(" ") };
};

export { isComplimentaryEventPaymentStatus };

export const isPaidEventPaymentStatus = (
  status: CmpEventPaymentStatusEnum,
): boolean =>
  status === CmpEventPaymentStatusEnum.Paid ||
  isComplimentaryEventPaymentStatus(status);

// Shared "Print/Download Invoice PDF" action (reuses the Invoicing backend).
export const getEventInvoicePdfVariables = ({
  chamberId,
  invoiceId,
}: {
  chamberId?: string | null;
  invoiceId?: string | null;
}) => {
  const normalizedChamberId = chamberId?.trim() || "";
  const normalizedInvoiceId = invoiceId?.trim() || "";

  if (!normalizedChamberId || !normalizedInvoiceId) {
    throw new Error(
      "The invoice PDF cannot be generated because the saved invoice could not be verified.",
    );
  }

  return {
    input: {
      chamberId: normalizedChamberId,
      invoiceId: normalizedInvoiceId,
    },
  };
};

export const useDownloadEventInvoicePdf = (chamberId: string) => {
  const [downloadPdf, { loading: downloadingPdf }] =
    useDownloadCmpInvoicePdfMutation({
      context: eventSalesCallerHandledApolloContext,
    });

  const downloadInvoicePdf = async (invoiceId?: string | null) => {
    const variables = getEventInvoicePdfVariables({ chamberId, invoiceId });
    const result = await downloadPdf({
      variables,
    });
    const download = result.data?.downloadCmpInvoicePdf.download;

    if (!download?.base64Data) {
      throw new Error("The invoice PDF could not be generated.");
    }

    downloadBase64File({
      base64Data: download.base64Data,
      contentType: download.contentType,
      filename: download.filename,
    });
  };

  return { downloadInvoicePdf, downloadingPdf };
};

// ---------------------------------------------------------------------------
// Shared status chip
// ---------------------------------------------------------------------------

export type ChipTone =
  | "green"
  | "amber"
  | "red"
  | "blue"
  | "gray"
  | "purple"
  | "sky";

const chipToneClassName: Record<ChipTone, string> = {
  green: "bg-green-100 text-green-800",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-800",
  blue: "bg-blue-100 text-blue-800",
  gray: "bg-gray-100 text-gray-700",
  purple: "bg-purple-100 text-purple-800",
  sky: "bg-sky-100 text-sky-800",
};

export const EventStatusChip = ({
  label,
  tone,
}: {
  label: string;
  tone: ChipTone;
}) => (
  <Chip
    label={label}
    size="small"
    className={`${chipToneClassName[tone]} font-semibold`}
  />
);

// ---------------------------------------------------------------------------
// Page section wrapper — facade over the shared SectionShell (B5
// InvoiceSection precedent): both sales pages render the platform section
// skin (black-border card + gold title pill) in one change. Titles and
// descriptions stay verbatim; description is a ReactNode so the manage-list
// sections can carry their bold tail.
// ---------------------------------------------------------------------------

export const EventSalesSection = ({
  children,
  action,
  id,
  title,
  description,
  required = false,
  variant = "card",
  showHeader = true,
  titleClassName,
  headerClassName,
  descriptionClassName,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
  id: string;
  title: string;
  description?: React.ReactNode;
  required?: boolean;
  variant?: "card" | "plain";
  showHeader?: boolean;
  titleClassName?: string;
  headerClassName?: string;
  descriptionClassName?: string;
}) => {
  if (variant === "plain") {
    return (
      <section id={id} className="scroll-mt-28">
        {showHeader ? (
          <div className={action ? "mb-[26px] flex flex-col gap-4 md:flex-row md:items-start md:justify-between" : headerClassName || "mb-[26px] flex flex-col gap-1.5"}>
            <div className="space-y-1.5">
            <h3
              className={
                titleClassName ||
                "text-[17px] font-bold leading-[22px] text-black"
              }
            >
              {title}
              {required ? (
                <span aria-hidden="true" className="text-red-500">
                  {" "}
                  *
                </span>
              ) : null}
            </h3>
            {description ? (
              <p
                className={
                  descriptionClassName ||
                  "text-[14px] leading-[18px] text-black"
                }
              >
                {description}
              </p>
            ) : null}
            </div>
            {action ? <div className="shrink-0 whitespace-nowrap">{action}</div> : null}
          </div>
        ) : null}
        {children}
      </section>
    );
  }

  return (
    <SectionShell id={id} title={title} description={description}>
      {children}
    </SectionShell>
  );
};

// ---------------------------------------------------------------------------
// Filter bar (B8's 56px search + Button/Menu filter design, built on the
// shared common/dashboard/FilterBar primitives; B7 ManageEvents precedent for
// the sx escape hatches — width:auto per instance, idle label #8F969E)
// ---------------------------------------------------------------------------

export type EventSalesFilterMenuConfig = {
  label: string;
  active: boolean;
  onOpen: (anchorEl: HTMLElement) => void;
  selectedLabels?: string[];
  width?: number;
};

export const eventSalesInvoiceFilterFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    height: 44,
    "& fieldset": {
      borderColor: "#9E9E9E",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: 14,
  },
  "& .MuiInputAdornment-root svg": {
    color: "#9E9E9E",
  },
} as const;

export const eventSalesInvoiceFilterButtonSx = {
  borderColor: "#9E9E9E",
  borderRadius: "8px",
  color: "#727272",
  fontSize: 14,
  fontWeight: 400,
  height: 42,
  justifyContent: "flex-start",
  lineHeight: "20px",
  px: "12px",
  width: "auto",
  "& .MuiButton-startIcon": {
    color: "#727272",
    ml: 0,
    mr: "8px",
    "& svg": { fontSize: 17 },
  },
  "&:hover": {
    backgroundColor: "white",
    borderColor: "#6F6F6F",
  },
} as const;

const renderEventSalesFilterLabel = (filter: EventSalesFilterMenuConfig) => {
  const selectedLabels = filter.selectedLabels || [];

  return (
    <span className="flex min-w-0 flex-1 items-center gap-1 text-left">
      <span className="truncate">{selectedLabels[0] || filter.label}</span>
      {selectedLabels.length > 1 ? (
        <span className="shrink-0">+{selectedLabels.length - 1}</span>
      ) : null}
      <KeyboardArrowDownIcon sx={{ fontSize: 16, ml: "auto" }} />
    </span>
  );
};

// The popup <Menu>s stay page-owned; this is the row layout, search field and
// trigger/clear buttons only (same split as the common FilterBar).
export const EventSalesFilterBar = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchMinWidth = 280,
  filters,
  onClearAllFilters,
}: {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  searchMinWidth?: number;
  filters: EventSalesFilterMenuConfig[];
  onClearAllFilters: () => void;
}) => (
  <FilterBar className="flex flex-wrap items-center gap-[14px]">
    <FilterSearchField
      value={searchValue}
      onChange={onSearchChange}
      placeholder={searchPlaceholder}
      className=""
      fullWidth={false}
      fieldSx={[
        filterFieldSx,
        eventSalesInvoiceFilterFieldSx,
        { flex: 1, minWidth: searchMinWidth },
      ]}
    />
    {filters.map((filter) => (
      <FilterMenuButton
        key={filter.label}
        label={renderEventSalesFilterLabel(filter)}
        onOpen={filter.onOpen}
        sx={[
          eventSalesInvoiceFilterButtonSx,
          ...(filter.width ? [{ flexShrink: 0, width: filter.width }] : []),
          { color: filter.active ? "#0A0A0A" : "#727272" },
        ]}
      />
    ))}
    {/* POL-013 (Q-026 answered: plural platform-wide). */}
    <ClearFiltersButton
      label="Clear All Filter"
      onClick={onClearAllFilters}
      sx={[clearFiltersButtonSx, { flexShrink: 0, width: 140 }]}
    />
  </FilterBar>
);

// ---------------------------------------------------------------------------
// Manage-list sortable headers (functional asc/desc toggle; B8's
// implementation promoted so B9's decorative affordances become real)
// ---------------------------------------------------------------------------

export type EventSalesSortState<Column extends string> = {
  column: Column;
  direction: "asc" | "desc";
};

export function toggleEventSalesSort<Column extends string>(
  current: EventSalesSortState<Column> | null,
  column: Column,
): EventSalesSortState<Column> | null {
  if (current?.column !== column) {
    return { column, direction: "asc" };
  }
  if (current.direction === "asc") {
    return { column, direction: "desc" };
  }
  return null;
}

export function EventSalesSortableHeader<Column extends string>({
  label,
  column,
  sort,
  onToggle,
}: {
  label: string;
  column: Column;
  sort: EventSalesSortState<Column> | null;
  onToggle: (column: Column) => void;
}): React.ReactElement {
  const isActive = sort?.column === column;
  return (
    <button
      type="button"
      onClick={() => onToggle(column)}
      className="inline-flex items-center gap-1 whitespace-nowrap font-bold leading-[18px] text-gray-950"
      aria-label={`Sort by ${label}`}
    >
      {label}
      <span className="relative h-4 w-[14px]" aria-hidden>
        <ArrowDropUpIcon
          sx={{
            left: -1,
            position: "absolute",
            top: -4,
            color:
              isActive && sort?.direction === "asc" ? "#111827" : "#9CA3AF",
            fontSize: 16,
          }}
        />
        <ArrowDropDownIcon
          sx={{
            left: -1,
            position: "absolute",
            top: 4,
            color:
              isActive && sort?.direction === "desc" ? "#111827" : "#9CA3AF",
            fontSize: 16,
          }}
        />
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// CSV download helpers (shared by both sales pages and Manage Events)
// ---------------------------------------------------------------------------

export const downloadCsvFile = ({
  csvContent,
  contentType,
  filename,
}: {
  csvContent: string;
  contentType: string;
  filename: string;
}) => downloadTextFile({ content: csvContent, contentType, excelCompatible: true, filename });

// Full attendee list from the backend export, saved with the design filename.
/**
 * The card payment went through, but the receipt read after it did not
 * finish. Staff must not take the payment again.
 */
export const eventCardPaymentReceiptUnavailableMessage =
  "The card payment was processed, but we could not show the receipt. Do not take the payment again. Refresh the page and check the invoice status.";

export const downloadEventAttendeeListCsv = (result: {
  data?: ExportCmpEventAttendeesCsvQuery;
  error?: unknown;
}) => {
  const csv = result.data?.exportCmpEventAttendeesCsv;
  if (!csv) {
    // The caller shows the Apollo error with getUserFacingErrorText.
    if (result.error) throw result.error;
    throw new Error("The attendee export did not return a file.");
  }
  downloadCsvFile({
    csvContent: csv.csvContent,
    contentType: csv.contentType,
    filename: eventAttendeeListCsvFilename,
  });
};

// ---------------------------------------------------------------------------
// Create-invoice line-item table chrome (blue 7-column header, shared by the
// ticket + sponsorship create forms; the single editable row is page-owned)
// ---------------------------------------------------------------------------

const invoiceLineItemHeadings = [
  "Code",
  "Description",
  "Qty",
  "Account #",
  "Unit Price",
  "Tax",
  "Total",
];

export const InvoiceLineItemTable = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <TableContainer className="rounded border border-gray-200">
    <Table size="small">
      <TableHead className="bg-[#2f7de1]">
        <TableRow>
          {invoiceLineItemHeadings.map((heading) => (
            <TableCell
              key={heading}
              className="!font-bold !uppercase !text-white"
            >
              {heading}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>{children}</TableRow>
      </TableBody>
    </Table>
  </TableContainer>
);

// Red square trash button on the line-item row (Claude token #F21B1B).
export const LineItemDeleteButton = ({
  onClick,
  ariaLabel = "delete line item",
}: {
  onClick: () => void;
  ariaLabel?: string;
}) => (
  <IconButton
    size="small"
    aria-label={ariaLabel}
    onClick={onClick}
    sx={{
      backgroundColor: invoiceDesignTokens.closeRed,
      borderRadius: "6px",
      color: "white",
      height: 30,
      padding: 0,
      width: 34,
      "&:hover": { backgroundColor: "#D90F1A" },
    }}
  >
    <DeleteOutlineIcon fontSize="small" />
  </IconButton>
);

// ---------------------------------------------------------------------------
// Analytics card row
// ---------------------------------------------------------------------------

export type EventStatCard = {
  label: string;
  value: string;
  // Small sub-value rendered INSIDE the card (mock: green "40/100" line).
  helper?: string;
  icon?: React.ReactNode;
  // Small bold footnote rendered BELOW the card row, under the card it
  // qualifies (mock: "*Includes unpaid , sponsorship and complimentary").
  footnote?: string;
};

const statRowXlColsClassName: Record<number, string> = {
  3: "xl:grid-cols-3",
  4: "xl:grid-cols-4",
  5: "xl:grid-cols-5",
  6: "xl:grid-cols-6",
};

// hideIcons renders the mock's text-only analytics cards (EVT-SALES-T-001 /
// S-001): bold grey label + large bold value, no icon anywhere. The default
// (false) keeps the Invoicing-module InvoiceSummaryCard rendering untouched.
export const EventStatCardRow = ({
  cards,
  hideIcons = false,
}: {
  cards: EventStatCard[];
  hideIcons?: boolean;
}) => {
  const gridClassName = `grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 ${
    statRowXlColsClassName[cards.length] || "xl:grid-cols-6"
  }`;

  if (!hideIcons) {
    return (
      <div className={gridClassName}>
        {cards.map((card) => (
          <InvoiceSummaryCard
            key={card.label}
            label={card.label}
            value={card.value}
            helper={card.helper}
            icon={card.icon}
          />
        ))}
      </div>
    );
  }

  // CMP Dashboard (21) Manage Tickets (:7262-7275, a footnote under each
  // card) and Manage Sponsorship (:7587-7593): text-only cards in a grid that
  // wraps (auto-fit), #ECECEC border, 8 px radius.
  const hasFootnotes = cards.some((card) => card.footnote);

  if (!hasFootnotes) {
    return (
      <div
        className="grid gap-2"
        data-event-stat-column-count={cards.length}
        style={{
          gridAutoRows: "auto",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gridTemplateRows: "auto auto",
        }}
      >
        {cards.map((card) => (
          <div
            key={card.label}
            className="grid content-start rounded-lg border border-[#ECECEC] bg-white px-[18px] py-4"
            style={{
              gap: 14,
              gridRow: "span 2",
              gridTemplateColumns: "minmax(0, 1fr)",
              gridTemplateRows: "subgrid",
            }}
          >
            <p className="text-[14px] font-normal leading-[18px] text-[#0A0A0A]">
              {card.label}
            </p>
            <div className="flex items-baseline gap-1.5 self-end">
              <p className="whitespace-nowrap text-[18px] font-bold leading-[23px] text-[#0A0A0A]">
                {card.value}
              </p>
              {card.helper ? (
                <p className="text-xs font-semibold text-[#10B981]">
                  {card.helper}
                </p>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className="grid gap-2"
      data-event-stat-column-count={cards.length}
      style={{
        gridAutoRows: "auto",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gridTemplateRows: "auto auto auto",
      }}
    >
      {cards.map((card) => (
        <div
          key={card.label}
          className="grid content-start"
          style={{
            gridRow: "span 3",
            gridTemplateColumns: "minmax(0, 1fr)",
            gridTemplateRows: "subgrid",
            rowGap: 10,
          }}
        >
          <div
            className="grid content-start rounded-lg border border-[#ECECEC] bg-white px-4 py-3.5"
            style={{
              gridRow: "span 2",
              gridTemplateColumns: "minmax(0, 1fr)",
              gridTemplateRows: "subgrid",
              rowGap: 14,
            }}
          >
            <p className="text-[14px] font-normal leading-[19px] text-[#0A0A0A]">
              {card.label}
            </p>
            <div className="flex items-baseline gap-1.5 self-end">
              <p className="whitespace-nowrap text-[18px] font-bold leading-[23px] text-[#0A0A0A]">
                {card.value}
              </p>
              {card.helper ? (
                <p className="text-xs font-semibold text-[#10B981]">
                  {card.helper}
                </p>
              ) : null}
            </div>
          </div>
          <p className="text-[11px] font-normal leading-[1.4] text-[#0A0A0A]">
            {card.footnote || ""}
          </p>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Event select dropdown
// ---------------------------------------------------------------------------

export type EventSelectOption = {
  id: string;
  title: string;
  eventCode?: string | null;
  startAtUtcMilli: number;
  endAtUtcMilli?: number | null;
  timeZone?: string | null;
  status: string;
};

/**
 * Event name as the issued invoice shows it: "{Event Code} - {Event Title}",
 * the same rule as the backend getCmpEventInvoiceDisplayName.
 */
export const getEventInvoiceDisplayName = (event?: {
  eventCode?: string | null;
  title?: string | null;
} | null): string => {
  const eventName = event?.title?.trim() || "Event";
  const eventCode = event?.eventCode?.trim();
  return eventCode ? `${eventCode} - ${eventName}` : eventName;
};

// Karen D31 (Monday 13254921501): Select Event shows the event name only;
// the issued invoice keeps "{Event Code} - {Event Title}".
export const getEventSelectOptionLabel = (
  option: EventSelectOption,
): string => option.title;

const hasEventStart = (option: EventSelectOption) =>
  Number.isFinite(option.startAtUtcMilli);

/**
 * Select Event lists events newest to oldest by start date and time; events
 * without a start come last, and ties sort by title (design D:8499-8508).
 */
export const sortEventSelectOptionsNewestFirst = (
  options: readonly EventSelectOption[],
): EventSelectOption[] =>
  [...options].sort((first, second) => {
    const firstHasStart = hasEventStart(first);
    const secondHasStart = hasEventStart(second);
    if (firstHasStart !== secondHasStart) return firstHasStart ? -1 : 1;
    const startDifference = firstHasStart
      ? second.startAtUtcMilli - first.startAtUtcMilli
      : 0;
    return startDifference || first.title.localeCompare(second.title);
  });

export type EventSelectStatusLabel = "Draft" | "Live" | "Closed" | "Cancelled";

/**
 * Status badge on Select Event (design (15) eventStatusLabel, Karen D31):
 * Cancelled and Draft come from the status; Closed is Completed or after
 * registration closes (the end of the event's last local day, the backend
 * rule that also opens "Event Already Closed"); every other event is Live.
 */
export const getEventSelectStatusLabel = (
  option: EventSelectOption,
  now = Date.now(),
): EventSelectStatusLabel => {
  if (option.status === CmpEventStatusEnum.Canceled) return "Cancelled";
  if (option.status === CmpEventStatusEnum.Draft) return "Draft";
  if (option.status === CmpEventStatusEnum.Completed) return "Closed";
  if (!hasEventStart(option)) return "Live";
  const eligibility = getStaffTicketEventEligibility(
    {
      status: option.status as CmpEventStatusEnum,
      startAtUtcMilli: option.startAtUtcMilli,
      endAtUtcMilli:
        typeof option.endAtUtcMilli === "number"
          ? option.endAtUtcMilli
          : option.startAtUtcMilli,
      timeZone: option.timeZone,
    },
    now,
  );
  return eligibility.closed ? "Closed" : "Live";
};

const EVENT_SELECT_STATUS_RANK: Record<EventSelectStatusLabel, number> = {
  Draft: 0,
  Live: 1,
  Closed: 2,
  Cancelled: 3,
};

// Design (15) SearchableSelect BADGE colours: [background, text].
const EVENT_SELECT_STATUS_BADGE: Record<EventSelectStatusLabel, [string, string]> = {
  Live: ["#ECFDF5", "#047857"],
  Closed: ["#F1F1F1", "#727272"],
  Cancelled: ["#FDECEC", "#B91C1C"],
  Draft: ["#FDECEC", "#B91C1C"],
};

/**
 * Design (15) orderedEventCatalog: Draft, Live, Closed, then Cancelled; in
 * each group newest to oldest by start (undated last, ties by title). Every
 * event stays selectable; a Closed pick on Create Ticket Invoice opens
 * "Event Already Closed".
 */
export const sortEventSelectOptionsByStatus = (
  options: readonly EventSelectOption[],
  now = Date.now(),
): EventSelectOption[] =>
  sortEventSelectOptionsNewestFirst(options)
    .map((option, index) => ({
      index,
      option,
      rank: EVENT_SELECT_STATUS_RANK[getEventSelectStatusLabel(option, now)],
    }))
    .sort((first, second) => first.rank - second.rank || first.index - second.index)
    .map(({ option }) => option);

/** Design (15) startsMatch: the event name starts with the search text. */
export const matchEventSelectOption = (
  option: EventSelectOption,
  term: string,
): boolean => option.title.trim().toLocaleLowerCase().startsWith(term);

const EventSelectOptionRow = ({
  option,
  now,
}: {
  option: EventSelectOption;
  now: number;
}) => {
  const status = getEventSelectStatusLabel(option, now);
  const [badgeBackground, badgeColor] = EVENT_SELECT_STATUS_BADGE[status];
  const muted = status === "Closed" || status === "Cancelled";

  return (
    <span
      style={{
        alignItems: "center",
        display: "flex",
        gap: 12,
        justifyContent: "space-between",
        minWidth: 0,
        width: "100%",
      }}
    >
      <span
        style={{
          color: muted ? "#9E9E9E" : "inherit",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {option.title}
      </span>
      <span
        data-event-status-badge={status}
        style={{
          background: badgeBackground,
          borderRadius: 6,
          color: badgeColor,
          flexShrink: 0,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: "18px",
          padding: "1px 8px",
        }}
      >
        {status}
      </span>
    </span>
  );
};

// No floating MUI label (EVT-SALES-T-004): optional static bold label above
// the control + grey "Select Event" placeholder text. Gold focus comes from
// the shared theme. Design (15): event name only (no event code), status
// badge on the right, and the field itself is the search box (inline-search)
// except on Manage Sponsorship Sales.
export const EventSelect = ({
  options,
  value,
  onChange,
  error,
  helperText,
  loading,
  label = "",
  placeholder = "Select Event",
  inlineSearch = true,
}: {
  options: EventSelectOption[];
  value: string;
  onChange: (eventId: string) => void;
  error?: boolean;
  helperText?: string;
  loading?: boolean;
  label?: string;
  placeholder?: string;
  inlineSearch?: boolean;
}) => {
  const selectedOption = options.find((option) => option.id === value) || null;
  const now = Date.now();
  const sortedOptions = useMemo(
    () => sortEventSelectOptionsByStatus(options),
    [options],
  );

  return (
    <InvoiceSearchableSelect
      getOptionKey={(option) => option.id}
      getOptionLabel={getEventSelectOptionLabel}
      error={error}
      helperText={helperText}
      id="event-sales-event"
      inlineSearch={inlineSearch}
      label={label}
      loading={Boolean(loading && options.length === 0)}
      loadingText="Loading events..."
      matchOption={matchEventSelectOption}
      noOptionsText="No events found"
      onChange={(option) => onChange(option?.id || "")}
      options={sortedOptions}
      outlinedArrow
      placeholder={placeholder}
      renderOption={(option) => <EventSelectOptionRow now={now} option={option} />}
      searchPlaceholder="Search Event"
      value={selectedOption}
    />
  );
};

// ---------------------------------------------------------------------------
// Member company + billing selector (shared by both invoice pages)
// ---------------------------------------------------------------------------

export type BillingDetails = {
  selectedMemberId: string;
  selectedMemberCompanyId: string;
  customerName: string;
  streetAddress: string;
  addressLine2: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  billingContactName: string;
  billingEmail: string;
  billingPhone: string;
};

export const blankBillingDetails = (): BillingDetails => ({
  selectedMemberId: "",
  selectedMemberCompanyId: "",
  customerName: "",
  streetAddress: "",
  addressLine2: "",
  city: "",
  province: "",
  postalCode: "",
  country: "Canada",
  billingContactName: "",
  billingEmail: "",
  billingPhone: "",
});

export const isEventSalesBillingDirty = (billing: BillingDetails): boolean => {
  const baseline = blankBillingDetails();

  return (Object.keys(baseline) as Array<keyof BillingDetails>).some(
    (field) => billing[field] !== baseline[field],
  );
};

export type MemberLookupOption = {
  id: string;
  chamberMemberId?: string | null;
  label: string;
  billingSnapshot: {
    customerName: string;
    contactName?: string | null;
    contactEmail: string;
    contactPhone?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    province?: string | null;
    postalCode?: string | null;
    country?: string | null;
  };
  // The member's company details for Sold To (null: same as billing).
  soldToSnapshot?: MemberLookupOption["billingSnapshot"] | null;
};

export const billingFromMemberOption = (
  option: MemberLookupOption,
): BillingDetails => ({
  selectedMemberId: option.id,
  selectedMemberCompanyId: option.chamberMemberId?.trim() || "",
  customerName: option.billingSnapshot.customerName,
  streetAddress: option.billingSnapshot.addressLine1 || "",
  addressLine2: option.billingSnapshot.addressLine2 || "",
  city: option.billingSnapshot.city || "",
  province: option.billingSnapshot.province || "",
  postalCode: option.billingSnapshot.postalCode || "",
  country: option.billingSnapshot.country || "",
  billingContactName:
    option.billingSnapshot.contactName || option.billingSnapshot.customerName,
  billingEmail: option.billingSnapshot.contactEmail,
  billingPhone: option.billingSnapshot.contactPhone || "",
});

export type CmpEventBillingInputLike = {
  companyName?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  streetAddress?: string;
  addressLine2?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
};

export const toCmpEventBillingInput = (
  billing: BillingDetails,
): CmpEventBillingInputLike => ({
  companyName: billing.customerName || undefined,
  contactName: billing.billingContactName || undefined,
  contactEmail: billing.billingEmail || undefined,
  contactPhone: billing.billingPhone || undefined,
  streetAddress: billing.streetAddress || undefined,
  addressLine2: billing.addressLine2 || undefined,
  city: billing.city || undefined,
  province: billing.province || undefined,
  postalCode: billing.postalCode || undefined,
  country: billing.country || undefined,
});

export const toCmpEventBillingInputFromSnapshot = (
  snapshot?: MemberLookupOption["billingSnapshot"] | null,
): CmpEventBillingInputLike | undefined =>
  snapshot
    ? {
        companyName: snapshot.customerName || undefined,
        contactName: snapshot.contactName || undefined,
        contactEmail: snapshot.contactEmail || undefined,
        contactPhone: snapshot.contactPhone || undefined,
        streetAddress: snapshot.addressLine1 || undefined,
        addressLine2: snapshot.addressLine2 || undefined,
        city: snapshot.city || undefined,
        province: snapshot.province || undefined,
        postalCode: snapshot.postalCode || undefined,
        country: snapshot.country || undefined,
      }
    : undefined;

export type MemberBillingMode = "member" | "non-member";

export type MemberBillingFieldErrors = Readonly<
  Partial<
    Record<
      | "billingCity"
      | "billingContactName"
      | "billingCountry"
      | "billingEmail"
      | "billingPhone"
      | "billingPostalCode"
      | "billingProvince"
      | "billingStreetAddress"
      | "companyName"
      | "member",
      string
    >
  >
>;

// Plain billing section shared by the ticket and sponsorship create pages.
// The selected member remains the source of the hidden suite, province, and
// country values. Missing province/country values are the only case where
// supplemental inputs appear. The billing address is optional for invoice
// issue (approved Oct 12 2026); missing fields are left off the invoice.
//
// - `mode`/`onModeChange`: member vs non-member purchaser toggle
//   (EVT-BLAIR-SALES-001), styled like module-5 CreateInvoice's segmented
//   "Existing member / Non-member" toggle. Non-member mode hides the member
//   dropdown and shows an editable Customer / Company Name field; billing
//   address/contact/email/phone stay editable in both modes. The toggle only
//   renders when `onModeChange` is provided.
// - `onSearchChange`: debounced server-side member search
//   (EVT-BLAIR-SALES-002) — the page owns the query and passes `members`.
export const MemberBillingSelector = ({
  members,
  billing,
  errors = {},
  onChange,
  onClearError,
  loading,
  mode = "member",
  onModeChange,
  onSearchChange,
  heading = "Select Member Company To Invoice",
  headingClassName = "text-base font-semibold text-gray-950",
  helperText = "Edit member contact, email and phone number, if different than what auto-populates.",
  memberLabelClassName = "text-[15px] font-semibold text-gray-950",
  billingFieldLabelClassName = "text-sm font-semibold text-gray-950",
  billingAddressHelperText = "Edit if not shown correctly",
}: {
  members: MemberLookupOption[];
  billing: BillingDetails;
  errors?: MemberBillingFieldErrors;
  onChange: (next: BillingDetails) => void;
  onClearError?: (...keys: string[]) => void;
  loading?: boolean;
  mode?: MemberBillingMode;
  onModeChange?: (mode: MemberBillingMode) => void;
  onSearchChange?: (search: string) => void;
  heading?: string;
  headingClassName?: string;
  helperText?: string;
  memberLabelClassName?: string;
  billingFieldLabelClassName?: string;
  billingAddressHelperText?: string;
}) => {
  const update = (patch: Partial<BillingDetails>, ...errorKeys: string[]) => {
    onChange({ ...billing, ...patch });
    if (errorKeys.length > 0) {
      onClearError?.(...errorKeys);
    }
  };

  // Keep the selected member renderable even while a new server-side search
  // narrows `members` to a list that no longer contains it.
  const [selectedOption, setSelectedOption] =
    useState<MemberLookupOption | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    },
    [],
  );

  const selectedMember =
    members.find((member) => member.id === billing.selectedMemberId) ||
    (selectedOption && selectedOption.id === billing.selectedMemberId
      ? selectedOption
      : null);
  const memberOptions =
    selectedMember && !members.some((member) => member.id === selectedMember.id)
      ? [selectedMember, ...members]
      : members;
  const showMissingProvinceField =
    mode === "non-member" || (Boolean(billing.selectedMemberId) && !billing.province.trim());
  const showMissingCountryField =
    mode === "non-member" || (Boolean(billing.selectedMemberId) && !billing.country.trim());
  const [showAddressLine2, setShowAddressLine2] = useState(false);

  const handleSearchInput = (search: string) => {
    if (!onSearchChange) {
      return;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(
      () => onSearchChange(search.trim()),
      350,
    );
  };

  return (
    <div className="space-y-[26px]">
      {/* Design (15) D:5294-5308, Karen D35: Sold To, then Select Company
          to Invoice, then Member / Customer. */}
      {onModeChange ? (
        <div className="flex flex-col gap-2">
          <p className="text-[15px] font-semibold leading-[20px] text-[#0A0A0A]">
            Sold To <span className="text-[#E24C4C]">*</span>
          </p>
          <div className="flex w-fit gap-1 rounded-[10px] border border-[#C7C7C7] bg-white p-1">
            {(
              [
                { value: "member", label: "Member" },
                {
                  value: "non-member",
                  label: "Non-member",
                },
              ] as const
            ).map((option) => {
              const isActive = mode === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onModeChange(option.value)}
                  aria-pressed={isActive}
                  className={`rounded-[7px] px-[22px] py-[9px] text-[15px] font-semibold leading-[normal] transition ${
                    isActive
                      ? "bg-[#E1A731] text-white"
                      : "bg-white text-[#0A0A0A]"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="space-y-1.5">
        <h3 className={headingClassName}>{heading}</h3>
        <p className="text-[14px] leading-[18px] text-[#0A0A0A]">
          {helperText}
        </p>
      </div>

      <div className="space-y-[26px]">
        {mode === "member" ? (
          <div className="max-w-[600px] space-y-2">
            <p className={memberLabelClassName}>
              Member <span className="text-red-500">*</span>
            </p>
            <p className="text-[14px] leading-[18px] text-[#0A0A0A]">
              Search or select the member from the drop down.
            </p>
            <InvoiceSearchableSelect
              error={Boolean(errors.member)}
              getOptionKey={(option) => option.id}
              getOptionLabel={(option) => option.label}
              helperText={errors.member ? "Member is required" : undefined}
              id="event-sales-member"
              loading={Boolean(loading && memberOptions.length === 0)}
              loadingText="Loading members..."
              noOptionsText="No members found"
              onChange={(option) => {
                setSelectedOption(option);
                if (option) {
                  onChange(billingFromMemberOption(option));
                  onClearError?.(
                    "member",
                    "companyName",
                    "billingStreetAddress",
                    "billingCity",
                    "billingProvince",
                    "billingPostalCode",
                    "billingCountry",
                    "billingContactName",
                    "billingEmail",
                    "billingPhone",
                  );
                } else {
                  update(
                    {
                      selectedMemberCompanyId: "",
                      selectedMemberId: "",
                    },
                    "member",
                  );
                }
              }}
              onSearchChange={onSearchChange ? handleSearchInput : undefined}
              options={memberOptions}
              placeholder="Select Member Name"
              searchMode={onSearchChange ? "server" : "client"}
              searchPlaceholder="Search Member"
              value={selectedMember}
            />
          </div>
        ) : (
          <div className="max-w-[600px] space-y-2">
            <p className={memberLabelClassName}>
              Customer / Company Name <span className="text-red-500">*</span>
            </p>
            <p className="text-[14px] leading-[18px] text-[#0A0A0A]">
              Enter the customer or company being invoiced.
            </p>
            <TextField
              error={Boolean(errors.companyName)}
              helperText={errors.companyName}
              placeholder="Enter Customer / Company Name"
              value={billing.customerName}
              onChange={({ target }) =>
                update({ customerName: target.value }, "companyName")
              }
              fullWidth
              sx={eventBillingFieldSx}
            />
          </div>
        )}

        <div className="space-y-2">
          <p className={billingFieldLabelClassName}>Billing/Mailing Address</p>
          <p className="text-[14px] leading-[18px] text-[#0A0A0A]">
            {billingAddressHelperText}
          </p>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <GoogleAddressTextField
              error={Boolean(errors.billingStreetAddress)}
              helperText={errors.billingStreetAddress}
              placeholder="Enter Street Number and Name"
              value={billing.streetAddress}
              onValueChange={(streetAddress) => update({ streetAddress }, "billingStreetAddress")}
              onAddressSelect={(address) => {
                setShowAddressLine2(true);
                update({ streetAddress: address.streetAddress,
                  addressLine2: address.unit ? `Unit ${address.unit}` : "",
                  city: address.city, province: address.province || address.provinceCode,
                  postalCode: address.postalCode, country: address.country },
                  "billingStreetAddress", "billingCity", "billingProvince", "billingPostalCode", "billingCountry");
              }}
              fullWidth
              sx={eventBillingFieldSx}
            />
            <TextField
              error={Boolean(errors.billingCity)}
              helperText={errors.billingCity}
              placeholder="Enter City Name"
              value={billing.city}
              onChange={({ target }) =>
                update({ city: target.value }, "billingCity")
              }
              fullWidth
              sx={eventBillingFieldSx}
            />
            <TextField
              error={Boolean(errors.billingPostalCode)}
              helperText={errors.billingPostalCode}
              placeholder="Enter Postal Code"
              value={billing.postalCode}
              onChange={({ target }) =>
                update({ postalCode: target.value }, "billingPostalCode")
              }
              fullWidth
              sx={eventBillingFieldSx}
            />
          </div>
          {showAddressLine2 || billing.addressLine2 ? (
            <TextField placeholder="Suite / Unit (Optional)" value={billing.addressLine2}
              onChange={({ target }) => update({ addressLine2: target.value })}
              fullWidth sx={eventBillingFieldSx} />
          ) : billing.selectedMemberId || mode === "non-member" ? (
            <button type="button" className="text-sm underline" onClick={() => setShowAddressLine2(true)}>Add suite / unit</button>
          ) : null}
          {showMissingProvinceField || showMissingCountryField ? (
            <div className="mt-3 grid grid-cols-1 gap-3 md:max-w-[600px] md:grid-cols-2">
              {showMissingProvinceField ? (
                <TextField
                  error={Boolean(errors.billingProvince)}
                  helperText={errors.billingProvince}
                  placeholder="Province / State"
                  value={billing.province}
                  onChange={({ target }) =>
                    update({ province: target.value }, "billingProvince")
                  }
                  fullWidth
                  sx={eventBillingFieldSx}
                />
              ) : null}
              {showMissingCountryField ? (
                <CountrySelectField
                  error={Boolean(errors.billingCountry)}
                  helperText={errors.billingCountry}
                  value={billing.country}
                  onValueChange={(country) =>
                    update({ country }, "billingCountry")
                  }
                  fullWidth
                  sx={eventBillingFieldSx}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <p className={billingFieldLabelClassName}>
              Billing Contact Name <span className="text-red-500">*</span>
            </p>
            <TextField
              error={Boolean(errors.billingContactName)}
              helperText={errors.billingContactName}
              placeholder="Enter Contact Name"
              value={billing.billingContactName}
              onChange={({ target }) =>
                update(
                  { billingContactName: target.value },
                  "billingContactName",
                )
              }
              fullWidth
              sx={eventBillingFieldSx}
            />
          </div>
          <div className="space-y-2">
            <p className={billingFieldLabelClassName}>
              Email <span className="text-red-500">*</span>
            </p>
            <TextField
              error={Boolean(errors.billingEmail)}
              helperText={errors.billingEmail}
              placeholder="Enter Contact Email"
              value={billing.billingEmail}
              onChange={({ target }) =>
                update({ billingEmail: target.value }, "billingEmail")
              }
              fullWidth
              sx={eventBillingFieldSx}
            />
          </div>
          <div className="space-y-2">
            <p className={billingFieldLabelClassName}>
              Phone <span className="text-red-500">*</span>
            </p>
            <TextField
              error={Boolean(errors.billingPhone)}
              helperText={errors.billingPhone}
              placeholder="Enter Phone Number"
              value={billing.billingPhone}
              onChange={({ target }) =>
                update({ billingPhone: formatStaffPhoneInput(target.value) }, "billingPhone")
              }
              fullWidth
              sx={eventBillingFieldSx}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Attendee assignment rows (Name / Email / Phone)
// ---------------------------------------------------------------------------

export type AttendeeAssignment = {
  companyName?: string;
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

let attendeeIdCounter = 0;
export const blankAttendee = (): AttendeeAssignment => {
  attendeeIdCounter += 1;
  return {
    id: `attendee-${attendeeIdCounter}`,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  };
};

// `attendeeFieldsOptional` keeps email optional for admin sales pages.
// `attendeeNameRequired` supports package tickets where every included seat
// must still have an attendee record.
export const AttendeeAssignmentList = ({
  attendees,
  onChange,
  helperText,
  attendeeFieldsOptional = false,
  attendeeNameRequired,
}: {
  attendees: AttendeeAssignment[];
  onChange: (next: AttendeeAssignment[]) => void;
  helperText?: string;
  attendeeFieldsOptional?: boolean;
  attendeeNameRequired?: boolean;
}) => {
  const update = (id: string, patch: Partial<AttendeeAssignment>) =>
    onChange(
      attendees.map((attendee) =>
        attendee.id === id ? { ...attendee, ...patch } : attendee,
      ),
    );

  const nameRequiredMark =
    attendeeNameRequired || !attendeeFieldsOptional ? (
      <span className="text-red-500"> *</span>
    ) : null;
  const emailRequiredMark = attendeeFieldsOptional ? null : (
    <span className="text-red-500"> *</span>
  );

  return (
    <div className="space-y-3">
      {helperText ? (
        <p className="text-sm text-gray-600">{helperText}</p>
      ) : null}
      <div className="hidden grid-cols-[28px_repeat(4,minmax(0,1fr))] gap-3 px-1 text-xs font-bold uppercase text-gray-500 md:grid">
        <span>#</span>
        <span>Attendee Name{nameRequiredMark}</span>
        <span>Attendee Company</span>
        <span>Attendee Email{emailRequiredMark}</span>
        <span>Attendee Phone Number</span>
      </div>
      {attendees.map((attendee, index) => (
        <div
          key={attendee.id}
          className="grid grid-cols-1 items-center gap-3 md:grid-cols-[28px_repeat(4,minmax(0,1fr))]"
        >
          <span className="text-sm font-semibold text-gray-500">
            {index + 1}
          </span>
          <div className="grid grid-cols-2 gap-2">
            <TextField
              size="small"
              placeholder="First Name"
              value={attendee.firstName}
              onChange={({ target }) =>
                update(attendee.id, { firstName: target.value })
              }
              fullWidth
            />
            <TextField
              size="small"
              placeholder="Last Name"
              value={attendee.lastName}
              onChange={({ target }) =>
                update(attendee.id, { lastName: target.value })
              }
              fullWidth
            />
          </div>
          <TextField size="small" placeholder="Enter Attendee Company" value={attendee.companyName || ""}
            inputProps={{ maxLength: 200 }} onChange={({ target }) => update(attendee.id, { companyName: target.value })} fullWidth />
          <TextField
            size="small"
            placeholder="attendee@email.com"
            value={attendee.email}
            onChange={({ target }) =>
              update(attendee.id, { email: target.value })
            }
            fullWidth
          />
          <TextField
            size="small"
            placeholder="Phone Number"
            value={attendee.phone}
            onChange={({ target }) =>
              update(attendee.id, { phone: formatStaffPhoneInput(target.value) })
            }
            fullWidth
          />
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Confirm dialog ("Are you sure ...")
// ---------------------------------------------------------------------------

// Mock confirm pattern: red circular X close, large RED circle with white
// question mark, bold centered copy, PROCEED/CANCEL style buttons.
// `confirmTone="blue"` gives the payment modals their blue PROCEED button.
export const ConfirmDialog = ({
  open,
  tone = "warning",
  title,
  message,
  confirmLabel = "Yes",
  cancelLabel = "Cancel",
  confirmTone = "gold",
  loading,
  errorMessage,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  tone?: "warning" | "danger" | "question";
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmTone?: "gold" | "blue";
  loading?: boolean;
  errorMessage?: string | null;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}) => {
  const [confirming, setConfirming] = useState(false);
  const [localErrorMessage, setLocalErrorMessage] = useState<string | null>(
    null,
  );
  const effectiveLoading = Boolean(loading) || confirming;
  const toneConfig = {
    warning: {
      bg: "bg-amber-500",
      icon: <WarningAmberIcon fontSize="large" />,
    },
    // Mock's "red-?" confirm pattern for destructive + confirm-payment flows.
    danger: { bg: "bg-red-500", icon: <HelpOutlineIcon fontSize="large" /> },
    question: { bg: "bg-red-500", icon: <HelpOutlineIcon fontSize="large" /> },
  }[tone];
  const confirmSx =
    confirmTone === "blue"
      ? {
          ...invoiceModalActionSx({
            backgroundColor: invoiceDesignTokens.blue,
            hoverBackgroundColor: "#0079D6",
          }),
        }
      : {
          ...invoiceModalActionSx({
            backgroundColor: cpaGold,
            hoverBackgroundColor: cpaGoldDark,
          }),
        };

  useEffect(() => {
    if (open) {
      setConfirming(false);
      setLocalErrorMessage(null);
    }
  }, [open, title]);

  const handleConfirm = async () => {
    if (effectiveLoading) {
      return;
    }

    setConfirming(true);
    setLocalErrorMessage(null);
    try {
      await onConfirm();
    } catch (error) {
      setLocalErrorMessage(
        getUserFacingErrorText(error, buildStaffErrorFallback("finish this action")),
      );
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={open}
      onClose={effectiveLoading ? undefined : onCancel}
      PaperProps={{ sx: invoiceDialogPaperSx({ maxWidth: 440 }) }}
    >
      <div
        className="relative flex flex-col items-center px-7 py-[30px] text-center"
        style={{ fontFamily: invoiceDesignFontFamily }}
      >
        <InvoiceDialogCloseButton
          ariaLabel="Close confirmation"
          disabled={effectiveLoading}
          onClick={onCancel}
        />
        <div className="flex flex-col items-center text-center">
          <div
            className={`flex h-16 w-16 items-center justify-center rounded-full ${toneConfig.bg} text-white`}
          >
            {toneConfig.icon}
          </div>
          {tone === "question" && !message ? (
            <p className="mx-auto mt-[18px] max-w-[340px] text-sm leading-[1.55] text-[#0A0A0A]">
              {title}
            </p>
          ) : (
            <>
              <h3 className="mt-[18px] text-sm font-semibold text-[#0A0A0A]">
                {title}
              </h3>
              {message ? (
                <p className="mx-auto mt-2 max-w-[340px] text-sm leading-[1.55] text-[#0A0A0A]">
                  {message}
                </p>
              ) : null}
            </>
          )}
          {localErrorMessage || errorMessage ? (
            <Alert className="mt-3 w-full text-left" severity="error">
              {localErrorMessage || errorMessage}
            </Alert>
          ) : null}
          <div className="mt-[22px] flex w-full flex-wrap justify-center gap-3">
            <Button
              variant="contained"
              disabled={effectiveLoading}
              onClick={() => void handleConfirm()}
              startIcon={
                effectiveLoading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : undefined
              }
              sx={{ ...confirmSx, flex: "1 1 0", maxWidth: 170 }}
            >
              {confirmLabel}
            </Button>
            <Button
              variant="contained"
              disabled={effectiveLoading}
              onClick={onCancel}
              sx={{
                ...invoiceModalActionSx({
                  backgroundColor: invoiceDesignTokens.dark,
                  hoverBackgroundColor: "#111827",
                }),
                flex: "1 1 0",
                maxWidth: 170,
              }}
            >
              {cancelLabel}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Result modal (success / failure)
// ---------------------------------------------------------------------------

// Result pop-up for Events and Event Sales: the Claude Design
// ActionSuccessModal (message-only card; red icon for failures).
export const ResultModal = ({
  open,
  success = true,
  title,
  message,
  highlights,
  onClose,
}: {
  open: boolean;
  success?: boolean;
  title: string;
  message?: string;
  highlights?: readonly string[];
  onClose: () => void;
}) => (
  <ActionSuccessDialog
    tone={success ? "success" : "error"}
    message={open ? (message ? `${title} ${message}` : title) : null}
    highlights={highlights}
    onClose={onClose}
  />
);

// ---------------------------------------------------------------------------
// Update Attendee Names modal (shared by both sales pages; the two visual
// variants preserve each page's existing DOM verbatim — "labeled" is the
// ticket page's "Attendee {n}" rows, "numbered" is the sponsorship page's
// index-column rows)
// ---------------------------------------------------------------------------

export type EditableAttendeeName = {
  companyName?: string | null;
  customFieldAnswers?: Record<string, string>;
  email?: string;
  id: string;
  firstName: string;
  lastName: string;
  phone?: string;
  /** Additional Details (staff notes). */
  staffNotes?: string;
  ticketTypeId?: string;
};


export const attendeeFieldRequiredMessage = "This field is required";
export const attendeeEmailInvalidMessage = "Enter a valid email address";

type AttendeeEditorFieldErrors = Readonly<Record<string, string>>;

/**
 * Update Attendee Details rules (Moeen, Sept 24 2026): every attendee needs a
 * name. Only attendee 1 (the first attendee of the whole record) needs an
 * email; other emails are optional but must be valid when typed.
 */
export const validateEditableAttendeeNames = (
  attendees: readonly EditableAttendeeName[],
): AttendeeEditorFieldErrors => {
  const errors: Record<string, string> = {};
  attendees.forEach((attendee, index) => {
    if (![attendee.firstName, attendee.lastName].join(" ").trim()) {
      errors[`${attendee.id}.name`] = attendeeFieldRequiredMessage;
    }
    const email = attendee.email?.trim() || "";
    if (index === 0 && !email) {
      errors[`${attendee.id}.email`] = attendeeFieldRequiredMessage;
    } else if (email && !isValidEventSalesEmail(email)) {
      errors[`${attendee.id}.email`] = attendeeEmailInvalidMessage;
    }
  });
  return errors;
};

export const UpdateAttendeeNamesModal = ({
  open,
  title,
  description,
  attendees,
  choiceFieldsForAttendee,
  onAttendeeChange,
  canDeleteAttendee,
  onDeleteAttendee,
  onClose,
  onSave,
  saving,
  emptyMessage,
  attendeeTicketLabel = "ticket",
  fieldSx,
  ticketGroups,
  nameOptions = [],
  companyOptions = [],
  additionalComments,
}: {
  open: boolean;
  title: string;
  description: string;
  attendees: EditableAttendeeName[];
  /**
   * Ticket type groups for a ticket invoice (design `groups`). Attendees are
   * shown under their ticket type and numbered per group; the header counts
   * seats. Without groups (sponsorships), attendees show as one numbered list.
   */
  ticketGroups?: readonly EventAttendeeTicketGroupHeader[];
  choiceFieldsForAttendee: (
    attendee: EditableAttendeeName,
  ) => EventAttendeeChoiceFields;
  onAttendeeChange: (
    id: string,
    patch: {
      companyName?: string;
      customFieldAnswers?: Record<string, string>;
      email?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      staffNotes?: string;
    },
  ) => void;
  canDeleteAttendee?: (attendee: EditableAttendeeName) => boolean;
  onDeleteAttendee?: (id: string) => void;
  onClose: () => void;
  onSave: () => Promise<void> | void;
  saving?: boolean;
  emptyMessage?: string;
  /** Delete confirmation wording: "from this <label>?" (design: "complimentary ticket"). */
  attendeeTicketLabel?: string;
  variant?: "labeled" | "numbered";
  fieldSx?: object;
  /** Attendee Name suggestions (previous attendees and member contacts). */
  nameOptions?: readonly EventAttendeeSuggestion[];
  /** Member company names for Attendee Company (design "MEMBERS"). */
  companyOptions?: readonly string[];
  /**
   * Sponsorship only: the buyer's website Step 6 Additional Comments, shown
   * read-only when present (Issue Fixer 11 Q25; no staff design).
   */
  additionalComments?: string | null;
}) => {
  const [deleteTarget, setDeleteTarget] = useState<EditableAttendeeName | null>(null);
  const saveGuard = useRef(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [saveInFlight, setSaveInFlight] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<AttendeeEditorFieldErrors>({});
  const effectiveSaving = Boolean(saving) || saveInFlight;
  const groups = useMemo(
    () =>
      ticketGroups
        ? groupEventAttendeesByTicketType({ attendees, headers: ticketGroups })
        : null,
    [attendees, ticketGroups],
  );
  // Display order is the validation order: attendee 1 is the first attendee
  // shown for the whole record.
  const orderedAttendees = useMemo(
    () => (groups ? groups.flatMap((group) => group.attendees) : attendees),
    [attendees, groups],
  );

  const clearFieldError = (attendeeId: string, field: "email" | "name") =>
    setFieldErrors((current) => {
      const key = `${attendeeId}.${field}`;
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });

  // The typed name text, kept per attendee while the modal is open. The
  // saved first/last names come from splitName, which trims, so rebuilding
  // the field from them would drop a space as soon as it is typed.
  const [nameDrafts, setNameDrafts] = useState<Readonly<Record<string, string>>>({});

  const getNameValue = (attendee: EditableAttendeeName) => {
    const draft = nameDrafts[attendee.id];
    if (draft !== undefined) {
      const name = splitName(draft);
      // Use the draft only while it still matches the attendee record.
      if (name.first === attendee.firstName && name.last === attendee.lastName) {
        return draft;
      }
    }
    return [attendee.firstName, attendee.lastName].filter(Boolean).join(" ");
  };

  const handleNameChange = (attendee: EditableAttendeeName, value: string) => {
    const name = splitName(value);
    setNameDrafts((current) => ({ ...current, [attendee.id]: value }));
    onAttendeeChange(attendee.id, {
      firstName: name.first,
      lastName: name.last,
    });
    clearFieldError(attendee.id, "name");
  };

  useEffect(() => {
    if (open) {
      saveGuard.current = false;
      setSaveInFlight(false);
      setSaveError(null);
      setDeleteTarget(null);
      setFieldErrors({});
      setNameDrafts({});
    }
  }, [open]);

  const handleSave = async () => {
    if (saveGuard.current || effectiveSaving || deleteTarget) {
      return;
    }
    // Save runs validation instead of being disabled (design trySave).
    const errors = validateEditableAttendeeNames(orderedAttendees);
    setFieldErrors(errors);
    const [firstErrorKey] = Object.keys(errors);
    if (firstErrorKey) {
      // Keys follow display order, so this brings the first error into view
      // in a long attendee list without adding UI.
      const separator = firstErrorKey.lastIndexOf(".");
      document
        .getElementById(
          `edit-${firstErrorKey.slice(0, separator)}-attendee-${firstErrorKey.slice(separator + 1)}`,
        )
        ?.focus();
      return;
    }

    saveGuard.current = true;
    setSaveInFlight(true);
    setSaveError(null);
    try {
      await onSave();
    } catch (error) {
      setSaveError(
        getUserFacingErrorText(error, buildStaffErrorFallback("save the attendee details")),
      );
    } finally {
      saveGuard.current = false;
      setSaveInFlight(false);
    }
  };

  const primaryAttendeeId = orderedAttendees[0]?.id;
  const renderedAttendees: Array<{
    attendee: EditableAttendeeName;
    ariaName: string;
    deleteLabel: string;
    heading: React.ReactNode;
    number: number;
  }> = groups
    ? groups.flatMap((group, groupIndex) => {
        const seatCount = getEventAttendeeTicketGroupSeatCount(group);
        return group.attendees.map((attendee, index) => {
          const position = index + 1;
          return {
            attendee,
            ariaName: `${group.label} attendee ${position}`,
            deleteLabel: `Delete ${group.label} attendee ${position}`,
            number: position,
            heading:
              index === 0 ? (
                <div
                  className={`mb-2 flex items-baseline justify-between gap-3 border-b border-[#ECECEC] pb-2 ${groupIndex > 0 ? "mt-2.5" : ""}`}
                  data-attendee-ticket-group={group.ticketTypeId}
                >
                  <span className="text-base font-bold leading-[normal] text-[#0A0A0A]">
                    {group.label}
                  </span>
                  <span className="whitespace-nowrap text-[13px] leading-[normal] text-[#727272]">
                    {seatCount} {seatCount === 1 ? "ticket" : "tickets"}
                  </span>
                </div>
              ) : null,
          };
        });
      })
    : attendees.map((attendee, index) => ({
        attendee,
        ariaName: `Attendee ${index + 1}`,
        deleteLabel: `Delete attendee ${index + 1}`,
        heading: null,
        number: index + 1,
      }));

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      onClose={effectiveSaving ? undefined : onClose}
      open={open}
      PaperProps={{
        sx: {
          ...invoiceDialogPaperSx({ maxWidth: 680 }),
          border: "1px solid #ECECEC",
        },
      }}
    >
      <div
        className="relative flex max-h-[92vh] min-h-0 flex-col"
        ref={modalContentRef}
        style={{ fontFamily: invoiceDesignFontFamily }}
      >
        <header className="relative px-[18px] pb-0 pt-[18px] sm:px-[26px] sm:pt-6">
          <InvoiceDialogCloseButton
            ariaLabel="Close attendee editor"
            disabled={effectiveSaving}
            onClick={onClose}
          />
          <h3 className="pr-9 text-[18px] font-bold leading-[normal] text-[#0A0A0A] sm:text-[21px]">
            {title}
          </h3>
          <p className="mt-1.5 text-sm leading-[normal] text-[#0A0A0A]">
            {description}
          </p>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-4 sm:px-[26px] sm:py-[18px]">
          {attendees.length === 0 && emptyMessage ? (
            <p className="text-sm text-[#4B5563]">{emptyMessage}</p>
          ) : null}
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          {additionalComments?.trim() ? (
            // Not in design: read-only, styled like the attendee row label and
            // the modal description.
            <div
              className="flex flex-col gap-1.5 border-b border-[#ECECEC] pb-6"
              data-sponsorship-additional-comments
            >
              <p className="m-0 text-[13px] font-bold uppercase leading-[normal] tracking-[.06em] text-[#0A0A0A]">
                Additional Comments
              </p>
              <p className="m-0 whitespace-pre-wrap break-words text-sm leading-[normal] text-[#0A0A0A]">
                {additionalComments}
              </p>
            </div>
          ) : null}
          {renderedAttendees.map(({ attendee, ariaName, deleteLabel, heading, number }, renderedIndex) => {
            const editorIds = {
              company: `edit-${attendee.id}-attendee-company`,
              dietary: `edit-attendee-${attendee.id}-dietary`,
              email: `edit-${attendee.id}-attendee-email`,
              name: `edit-${attendee.id}-attendee-name`,
              phone: `edit-${attendee.id}-attendee-phone-number`,
              speaker: `edit-attendee-${attendee.id}-speaker`,
              "staff-notes": `edit-attendee-${attendee.id}-staff-notes`,
            } as const;

            // Design UpdateAttendeeNamesModal.jsx:154-238: the same attendee
            // fields as Create Ticket Invoice (IF11 EV7).
            return (
              <div
                className={`flex flex-col gap-[18px] pb-6 pt-[22px] ${
                  renderedIndex === renderedAttendees.length - 1 ? "" : "border-b border-[#ECECEC]"
                }`}
                data-attendee-editor-row={attendee.id}
                key={attendee.id}
              >
                {heading}
                <div className="flex items-center justify-between gap-3">
                  <p className="m-0 text-[13px] font-bold uppercase leading-[normal] tracking-[.06em] text-[#0A0A0A]">
                    Attendee {number}
                  </p>
                  {onDeleteAttendee && canDeleteAttendee?.(attendee) ? (
                    <Button
                      aria-label={deleteLabel}
                      disabled={effectiveSaving}
                      onClick={() => setDeleteTarget(attendee)}
                      startIcon={<DeleteOutlineIcon sx={{ fontSize: "12px !important" }} />}
                      sx={{
                        backgroundColor: "#F21B1B", color: "#fff", borderRadius: "6px",
                        fontFamily: invoiceDesignFontFamily, fontSize: 12, fontWeight: 700,
                        minWidth: 0, padding: "6px 12px", lineHeight: "normal", textTransform: "none",
                        "&:hover": { backgroundColor: "#D81414" },
                      }}
                    >Delete</Button>
                  ) : null}
                </div>
                <EventAttendeeDetailFields
                  ariaName={ariaName}
                  choiceFields={choiceFieldsForAttendee(attendee)}
                  companyOptions={companyOptions}
                  disabled={effectiveSaving}
                  emailError={fieldErrors[`${attendee.id}.email`]}
                  fieldId={(suffix) => editorIds[suffix]}
                  fieldSx={fieldSx}
                  nameError={fieldErrors[`${attendee.id}.name`]}
                  number={renderedIndex + 1}
                  onChange={({ name, ...patch }) => {
                    if (name !== undefined) handleNameChange(attendee, name);
                    if (Object.keys(patch).length > 0) onAttendeeChange(attendee.id, patch);
                  }}
                  onClearError={(field) => clearFieldError(attendee.id, field)}
                  onPickSuggestion={(suggestion) => {
                    handleNameChange(attendee, suggestion.name);
                    onAttendeeChange(attendee.id, {
                      email: suggestion.email || attendee.email || "",
                      phone: suggestion.phone
                        ? formatStaffPhoneInput(suggestion.phone)
                        : attendee.phone || "",
                    });
                  }}
                  primary={attendee.id === primaryAttendeeId}
                  suggestions={nameOptions}
                  values={{
                    companyName: attendee.companyName,
                    customFieldAnswers: attendee.customFieldAnswers || {},
                    email: attendee.email || "",
                    name: getNameValue(attendee),
                    phone: attendee.phone || "",
                    staffNotes: attendee.staffNotes,
                  }}
                />
              </div>
            );
          })}
        </div>

        <footer className="flex flex-wrap justify-end gap-3 border-t border-[#ECECEC] px-[18px] py-3.5 sm:px-[26px] sm:py-[18px]">
          <Button
            disabled={effectiveSaving}
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: cpaGold,
              borderRadius: "8px",
              color: cpaGold,
              fontFamily: invoiceDesignFontFamily,
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "normal",
              lineHeight: "normal",
              minHeight: 0,
              minWidth: 0,
              padding: "11px 24px",
              textTransform: "none",
              "&:hover": { borderColor: cpaGoldDark },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={effectiveSaving || Boolean(deleteTarget)}
            onClick={() => void handleSave()}
            startIcon={
              effectiveSaving ? (
                <CircularProgress color="inherit" size={16} />
              ) : undefined
            }
            sx={{
              ...invoiceModalActionSx({
                backgroundColor: cpaGold,
                hoverBackgroundColor: cpaGoldDark,
              }),
              fontWeight: 700,
              letterSpacing: "normal",
              lineHeight: "normal",
              minHeight: 0,
              minWidth: 0,
              padding: "11px 28px",
            }}
          >
            Save
          </Button>
        </footer>
      </div>
      <Dialog
        aria-labelledby="delete-event-attendee-title"
        container={() => modalContentRef.current}
        sx={{ position: "absolute" }}
        BackdropProps={{ sx: { position: "absolute", backgroundColor: "rgba(10,10,10,.45)" } }}
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        maxWidth={false}
        PaperProps={{ sx: { ...invoiceDialogPaperSx({ maxWidth: 420 }), border: "1px solid #ECECEC" } }}
      >
        <div className="flex flex-col items-center px-[26px] pb-6 pt-[30px] text-center" style={{ fontFamily: invoiceDesignFontFamily }}>
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F21B1B] text-white">
            <DeleteOutlineIcon sx={{ fontSize: 26 }} />
          </div>
          <h3 id="delete-event-attendee-title" className="mt-4 text-base font-bold text-[#0A0A0A]">Delete Attendee?</h3>
          <p className="mt-2 text-sm leading-[1.55] text-[#0A0A0A]">
            {/* Claude Design (13) ds-components/UpdateAttendeeNamesModal.jsx:230. */}
            Are you sure you want to delete <strong>{[deleteTarget?.firstName, deleteTarget?.lastName].filter(Boolean).join(" ") || "this attendee"}</strong>
            {" "}from this {attendeeTicketLabel}? This cannot be undone.
          </p>
          <div className="mt-[22px] flex w-full justify-center gap-3">
            <Button onClick={() => setDeleteTarget(null)} variant="outlined" sx={{ flex: "1 1 0", maxWidth: 160, borderColor: "#9E9E9E", color: "#0A0A0A", borderRadius: "8px", fontFamily: invoiceDesignFontFamily, fontSize: 14, fontWeight: 600, letterSpacing: "normal", lineHeight: "normal", textTransform: "none", padding: "11px 20px" }}>Cancel</Button>
            <Button onClick={() => {
              if (deleteTarget && canDeleteAttendee?.(deleteTarget)) onDeleteAttendee?.(deleteTarget.id);
              setDeleteTarget(null);
            }} variant="contained" sx={{ ...invoiceModalActionSx({ backgroundColor: "#F21B1B", hoverBackgroundColor: "#D81414" }), flex: "1 1 0", maxWidth: 160, fontWeight: 700, letterSpacing: "normal", lineHeight: "normal", boxShadow: "none", padding: "11px 20px" }}>Delete</Button>
          </div>
        </div>
      </Dialog>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Process Payment modal (Open Invoice -> record manual payment)
// ---------------------------------------------------------------------------

export type ManualPaymentMethodOption = {
  id: string;
  label: string;
  enabled: boolean;
};

export type ProcessPaymentSubmit = {
  amountInCents: number;
  manualPaymentMethodId: string;
  receivedAtUtcMilli: number;
  reference: string;
  note: string;
};

export type SecureCardCheckoutRenderInput = {
  amountInCents: number;
  /** Internal Notes, shown above the card form's Confirm & Record Payment. */
  beforeSubmit: React.ReactNode;
  /** Internal Notes, saved on the card payment before the card is submitted. */
  cardNote: string;
  /**
   * The card panel reports a card payment in progress (submit until the sale
   * shows the payment or Stripe returns an error). The popup then locks close
   * and method so staff cannot open a second checkout (UX audit INV-1).
   */
  onPaymentInProgressChange: (inProgress: boolean) => void;
};

export const isEventManualPaymentSubmitDisabled = ({
  amountDueInCents,
  amountInCents,
  loading,
  manualPaymentMethodId,
  paymentDate,
}: {
  amountDueInCents: number;
  amountInCents: number;
  loading: boolean;
  manualPaymentMethodId: string;
  paymentDate: string;
}): boolean =>
  loading ||
  !manualPaymentMethodId ||
  !isCalendarDateValue(paymentDate) ||
  amountInCents <= 0 ||
  amountInCents > amountDueInCents;

export type EventPaymentInvoiceLineItem = {
  id?: string;
  itemCode?: string | null;
  description: string;
  quantity: number;
  unitPriceInCents: number;
  subtotalAmountInCents?: number | null;
  taxAmountInCents?: number | null;
  totalAmountInCents: number;
};

// The payment amount uses the same compact geometry as the rendered Claude
// Process Payment fields.
const paymentModalCurrencyInputClassName =
  "h-[38px] w-full rounded-lg border border-[#D7DBE0] bg-white pl-7 pr-3 text-sm leading-[18px] text-[#0A0A0A] outline-none transition placeholder:text-[#8A8A8A] focus:border-cpaGold focus:ring-1 focus:ring-amber-100";

const eventCreditCardPaymentMethodId = "CMP_STRIPE_CREDIT_CARD";
export const eventProcessPaymentDialogMaxWidth = 700;
export const eventProcessPaymentFieldSx = invoiceProcessPaymentFieldSx;

export const ProcessPaymentModal = ({
  open,
  customerName,
  invoiceNumber,
  amountDueInCents,
  paymentMethods,
  loading,
  onSubmit,
  onClose,
  creditCardAvailable = false,
  cardCheckoutUnavailableReason,
  paymentActionUnavailableReason,
  requireManualPaymentConfirmation = true,
  renderSecureCardCheckout,
}: {
  open: boolean;
  customerName: string;
  invoiceNumber: string;
  amountDueInCents: number;
  invoiceLineItems?: EventPaymentInvoiceLineItem[];
  subtotalAmountInCents?: number | null;
  taxAmountInCents?: number | null;
  totalAmountInCents?: number | null;
  /** Staff-recordable methods from getStaffPaymentMethodOptions. */
  paymentMethods: readonly StaffPaymentMethodOption[];
  loading?: boolean;
  onSubmit: (payload: ProcessPaymentSubmit) => Promise<void> | void;
  onClose: () => void;
  creditCardAvailable?: boolean;
  cardCheckoutUnavailableReason?: string | null;
  paymentActionUnavailableReason?: string | null;
  requireManualPaymentConfirmation?: boolean;
  renderSecureCardCheckout?: (
    input: SecureCardCheckoutRenderInput,
  ) => React.ReactNode;
}) => {
  // The caller resolves which methods staff can record for this invoice,
  // in design order, through the shared staff payment method helper.
  const enabledManualMethods = useMemo(
    () =>
      paymentMethods.map((method) => ({
        enabled: true,
        id: method.id as string,
        label: method.label,
      })),
    [paymentMethods],
  );
  const cardCheckoutAvailable = Boolean(
    creditCardAvailable && renderSecureCardCheckout,
  );
  const enabledMethods = useMemo(
    () => [
      ...(cardCheckoutAvailable
        ? [
            {
              enabled: true,
              id: eventCreditCardPaymentMethodId,
              label: "Credit Card",
            },
          ]
        : []),
      ...enabledManualMethods,
    ],
    [cardCheckoutAvailable, enabledManualMethods],
  );
  const enabledMethodsKey = enabledMethods
    .map((method) => `${method.id}:${method.label}`)
    .join("\u0000");
  const [methodId, setMethodId] = useState("");
  const [paymentDate, setPaymentDate] = useState(getLocalDateInputValue());
  const [amount, setAmount] = useState(centsToDollarsString(amountDueInCents));
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [cardPaymentInProgress, setCardPaymentInProgress] = useState(false);
  const submitInFlightRef = useRef(false);
  const effectiveLoading = Boolean(loading) || submitting;
  const amountInCents = dollarsToCents(amount);
  const isCreditCardSelected = methodId === eventCreditCardPaymentMethodId;
  const manualPaymentSubmitDisabled = isEventManualPaymentSubmitDisabled({
    amountDueInCents,
    amountInCents,
    loading: effectiveLoading,
    manualPaymentMethodId: methodId,
    paymentDate,
  });

  useEffect(() => {
    if (open) {
      setMethodId("");
      setPaymentDate(getLocalDateInputValue());
      setAmount(centsToDollarsString(amountDueInCents));
      setReference("");
      setNote("");
      setConfirmOpen(false);
      setSubmitting(false);
      setSubmissionError(null);
      setCardPaymentInProgress(false);
      submitInFlightRef.current = false;
    }
  }, [open, amountDueInCents, enabledMethodsKey]);

  const handleConfirm = async () => {
    const receivedAtUtcMilli = localDateInputToUtcMilli(paymentDate);

    if (
      isCreditCardSelected ||
      submitInFlightRef.current ||
      !methodId ||
      !receivedAtUtcMilli ||
      amountInCents <= 0 ||
      amountInCents > amountDueInCents
    ) {
      return;
    }

    submitInFlightRef.current = true;
    setSubmitting(true);
    setSubmissionError(null);
    try {
      await onSubmit({
        amountInCents,
        manualPaymentMethodId: methodId,
        receivedAtUtcMilli,
        reference,
        note,
      });
      setConfirmOpen(false);
    } catch (error) {
      // The result is not known, so staff check the status before a retry.
      setSubmissionError(
        getUserFacingErrorText(
          error,
          buildStaffCheckFirstFallback(
            "record the payment",
            "Refresh the page and check the invoice status",
          ),
        ),
      );
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const internalNotesField = (
    <div>
      <p className="mb-1.5 text-[11.5px] font-semibold uppercase leading-[15px] tracking-[0.05em] text-[#0A0A0A]">
        Internal Notes
      </p>
      <TextField
        fullWidth
        minRows={2}
        multiline
        onChange={({ target }) => setNote(target.value)}
        placeholder="Additional details for bookkeeping..."
        sx={eventProcessPaymentFieldSx}
        value={note}
      />
    </div>
  );
  const cardFormShowsNotes = isCreditCardSelected && !!renderSecureCardCheckout && amountInCents > 0;

  return (
    <>
      <ProcessPaymentDialogShell
        afterPaymentMethods={
          // Design ProcessPaymentModal.jsx:118-146: the card fields sit under
          // CARD INFORMATION (the card panel's heading) and Internal Notes sits
          // above Confirm & Record Payment. A card payment saves the note just
          // before the card is submitted.
          <div className="space-y-3">
            {isCreditCardSelected ? (
              renderSecureCardCheckout && amountInCents > 0 ? (
                renderSecureCardCheckout({
                  amountInCents,
                  beforeSubmit: internalNotesField,
                  cardNote: note,
                  onPaymentInProgressChange: setCardPaymentInProgress,
                })
              ) : (
                <Alert severity="error">
                  Card payment is not available for this invoice. Check that the
                  amount to pay is more than $0.00, or select another payment
                  method.
                </Alert>
              )
            ) : (
              <div>
                <p className="mb-1.5 text-[11.5px] font-semibold uppercase leading-[15px] tracking-[0.05em] text-[#0A0A0A]">
                  Reference Number (Cheque # or Transaction ID)
                </p>
                <TextField
                  fullWidth
                  onChange={({ target }) => setReference(target.value)}
                  placeholder="Enter Reference number and details"
                  sx={eventProcessPaymentFieldSx}
                  value={reference}
                />
              </div>
            )}
            {cardFormShowsNotes ? null : internalNotesField}
          </div>
        }
        balanceOwing={formatCurrencyFromCents(amountDueInCents)}
        billTo={customerName}
        busy={effectiveLoading || cardPaymentInProgress}
        alerts={
          submissionError || paymentActionUnavailableReason || cardCheckoutUnavailableReason ? (
            <>
              {submissionError ? (
                <Alert severity="error">{submissionError}</Alert>
              ) : null}
              {paymentActionUnavailableReason || cardCheckoutUnavailableReason ? (
                <Alert severity="warning">
                  {paymentActionUnavailableReason || cardCheckoutUnavailableReason}
                </Alert>
              ) : null}
            </>
          ) : undefined
        }
        emptyPaymentMethodMessage={paymentActionUnavailableReason}
        invoiceNumber={invoiceNumber}
        onClose={onClose}
        onConfirm={
          isCreditCardSelected
            ? undefined
            : () => {
                if (requireManualPaymentConfirmation) {
                  setConfirmOpen(true);
                  return;
                }

                void handleConfirm();
              }
        }
        onPaymentMethodChange={(nextMethodId) => {
          if (cardPaymentInProgress) {
            return;
          }

          setMethodId(nextMethodId);
          if (nextMethodId === eventCreditCardPaymentMethodId) {
            setAmount(centsToDollarsString(amountDueInCents));
          }
        }}
        open={open && !(requireManualPaymentConfirmation && confirmOpen)}
        paymentDetails={
          <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2">
            {/* Stripe records a card payment when the card is charged, so
                the card Payment Date shows today, read-only. */}
            <div>
              <p className="mb-1.5 text-[11.5px] font-semibold uppercase leading-[15px] tracking-[0.05em] text-[#0A0A0A]">
                Payment Date
              </p>
              <CalendarDateInput
                ariaLabel="Payment Date"
                mode="fullDate"
                iconPosition="left"
                disabled={isCreditCardSelected}
                onChange={setPaymentDate}
                value={
                  isCreditCardSelected ? getLocalDateInputValue() : paymentDate
                }
              />
            </div>
            <div>
              <p className="mb-1.5 text-[11.5px] font-semibold uppercase leading-[15px] tracking-[0.05em] text-[#0A0A0A]">
                Amount to Pay
              </p>
              <CurrencyInput
                adornmentClassName="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#0A0A0A]"
                ariaLabel="Amount to pay"
                inputClassName={paymentModalCurrencyInputClassName}
                onChange={setAmount}
                readOnly={isCreditCardSelected}
                value={amount}
              />
            </div>
          </div>
        }
        paymentMethods={enabledMethods}
        primaryActionDisabled={manualPaymentSubmitDisabled}
        selectedPaymentMethodId={methodId}
      />

      <ConfirmDialog
        open={
          requireManualPaymentConfirmation &&
          confirmOpen &&
          !isCreditCardSelected
        }
        tone="question"
        title="Are you sure you want to process payment for this invoice?"
        confirmLabel="Proceed"
        cancelLabel="Cancel"
        confirmTone="blue"
        errorMessage={submissionError}
        loading={effectiveLoading}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

// Claude Design (15) invoice notes for event sponsorship invoices (Monday
// 13252657022, 13252657528). Issued invoices get the same text from the
// backend (cap-backend cmpSponsorshipInvoiceNotes.ts) from the sale record.
export const getSponsorshipComplimentaryTicketNote = (count: number): string | null =>
  Number.isInteger(count) && count > 0
    ? `Includes ${count} complimentary ticket${count === 1 ? "" : "s"}`
    : null;

export const getSponsorshipDraftTaxNote = ({
  mode,
  taxInCents,
}: {
  mode?: CmpEventSponsorshipTaxModeEnum | null;
  taxInCents: number;
}): string | null => {
  if (!mode || mode === CmpEventSponsorshipTaxModeEnum.Full) return null;
  return taxInCents > 0
    ? "Tax charged on complimentary tickets only."
    : "Sponsorships for this event are not subject to tax.";
};
